import type {
  ActivityEvent,
  ActivityPage,
  Agent,
  AppApiResponse,
  ArchivePage,
  AtomicTask,
  AuthSession,
  AuthUser,
  ByokGrant,
  ClarificationQuestion,
  ClarificationPayload,
  CreditLedger,
  Goal,
  Integration,
  IpAllowlistEntry,
  LlmRoutingConfig,
  MfaChallenge,
  NotificationPreference,
  PendingAssignment,
  Project,
  RemoteAgentProtocol,
  ReportsSnapshot,
  Subtask,
  SuperAdminAuditEvent,
  SuperAdminOverview,
  SuperAdminUser,
  SuperAdminWorkspace,
  SystemHealthSnapshot,
  Task,
  TaskOperation,
  TaskOutput,
  TaskSnapshot,
  Workspace,
  WorkspaceMember,
  WorkspaceSwitchResult
} from "@shared/domain";
import type { CreateTaskInput } from "@shared/schemas";
import type { RemoteAgentConnectionTest, RemoteAgentCredentials } from "@shared/remoteAgents";
import type { AiProviderId, AiSettings, SystemPromptKey } from "@shared/aiSettings";

export interface RemoteAgentConnectionInput {
  baseUrl: string;
  protocol: "auto" | RemoteAgentProtocol;
  model?: string;
  credentials: RemoteAgentCredentials;
}

export interface RemoteAgentUpdateInput {
  name?: string;
  baseUrl?: string;
  protocol?: "auto" | RemoteAgentProtocol;
  model?: string;
  credentials?: RemoteAgentCredentials;
  status?: "active" | "disconnected";
  test?: boolean;
}

const API_BASE = import.meta.env.VITE_API_BASE_URL || "/api";
const AUTH_ACCESS_TOKEN_KEY = "taskkar-api-access-token-v2";
const AUTH_CSRF_TOKEN_KEY = "taskkar-api-csrf-token-v2";

export class ApiClientError extends Error {
  status?: number;
  kind: "network" | "http" | "parse" | "app";

  constructor(message: string, options: { status?: number; kind: "network" | "http" | "parse" | "app" }) {
    super(message);
    this.name = "ApiClientError";
    this.status = options.status;
    this.kind = options.kind;
  }
}

const readStorage = (key: string): string | undefined => {
  try {
    const value = window.localStorage.getItem(key);
    return value && value.length > 0 ? value : undefined;
  } catch {
    return undefined;
  }
};

const writeStorage = (key: string, value?: string): void => {
  try {
    if (!value) {
      window.localStorage.removeItem(key);
      return;
    }
    window.localStorage.setItem(key, value);
  } catch {
    // no-op
  }
};

let authAccessToken: string | undefined = readStorage(AUTH_ACCESS_TOKEN_KEY);
let authCsrfToken: string | undefined = readStorage(AUTH_CSRF_TOKEN_KEY);
let authFailureHandler: (() => void) | undefined;

export const setApiAuthSession = (session: Pick<AuthSession, "accessToken" | "csrfToken"> | null | undefined): void => {
  authAccessToken = session?.accessToken;
  authCsrfToken = session?.csrfToken;
  writeStorage(AUTH_ACCESS_TOKEN_KEY, authAccessToken);
  writeStorage(AUTH_CSRF_TOKEN_KEY, authCsrfToken);
};

export const clearApiAuthSession = (): void => {
  setApiAuthSession(undefined);
};

export const setApiAuthFailureHandler = (handler: (() => void) | undefined): void => {
  authFailureHandler = handler;
};

const isMutatingMethod = (method: string | undefined): boolean => {
  const normalized = (method || "GET").toUpperCase();
  return normalized === "POST" || normalized === "PUT" || normalized === "PATCH" || normalized === "DELETE";
};

const readApiResponse = async <T>(res: Response): Promise<AppApiResponse<T>> => {
  try {
    return (await res.json()) as AppApiResponse<T>;
  } catch {
    throw new ApiClientError("Invalid API response format.", { status: res.status, kind: "parse" });
  }
};

const isRefreshablePath = (path: string): boolean => {
  if (path.startsWith("/auth/refresh")) return false;
  if (path.startsWith("/auth/email/login")) return false;
  if (path.startsWith("/auth/email/register")) return false;
  if (path.startsWith("/auth/email/verify")) return false;
  return true;
};

const doRefresh = async (): Promise<boolean> => {
  try {
    const res = await fetch(`${API_BASE}/auth/refresh`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json"
      }
    });
    if (!res.ok) {
      clearApiAuthSession();
      return false;
    }
    const payload = await readApiResponse<{ session: AuthSession }>(res);
    if (payload.error || !payload.data?.session) {
      clearApiAuthSession();
      return false;
    }
    setApiAuthSession(payload.data.session);
    return true;
  } catch {
    clearApiAuthSession();
    return false;
  }
};

async function request<T>(path: string, init?: RequestInit, options?: { retryAuth?: boolean; signal?: AbortSignal }): Promise<T> {
  const retryAuth = options?.retryAuth !== false;
  const headers = new Headers(init?.headers ?? undefined);
  const method = (init?.method || "GET").toUpperCase();
  if (!headers.has("Content-Type") && init?.body !== undefined) {
    headers.set("Content-Type", "application/json");
  }
  if (authAccessToken && !headers.has("Authorization")) {
    headers.set("Authorization", `Bearer ${authAccessToken}`);
  }
  if (isMutatingMethod(method) && authCsrfToken && !headers.has("x-csrf-token")) {
    headers.set("x-csrf-token", authCsrfToken);
  }

  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      credentials: "include",
      ...init,
      method,
      headers,
      signal: options?.signal ?? init?.signal
    });
  } catch (error) {
    if (
      (typeof DOMException !== "undefined" && error instanceof DOMException && error.name === "AbortError") ||
      (error instanceof Error && error.name === "AbortError")
    ) {
      throw error;
    }
    const message = error instanceof Error ? error.message : "Network request failed";
    throw new ApiClientError(message, { kind: "network" });
  }

  if (res.status === 401 && retryAuth && isRefreshablePath(path)) {
    const refreshed = await doRefresh();
    if (refreshed) {
      return request<T>(path, init, { retryAuth: false, signal: options?.signal });
    }
    authFailureHandler?.();
  }

  if (res.status === 403 && retryAuth && isRefreshablePath(path)) {
    const errorBody = await res.clone().text();
    if (/csrf token missing or invalid/i.test(errorBody)) {
      const refreshed = await doRefresh();
      if (refreshed) {
        return request<T>(path, init, { retryAuth: false, signal: options?.signal });
      }
      authFailureHandler?.();
    }
  }

  if (!res.ok) {
    if (res.status === 401 && !path.startsWith("/auth/")) {
      authFailureHandler?.();
    }
    const raw = await res.text();
    let message = raw || `Request failed: ${res.status}`;
    if (raw) {
      try {
        const parsed = JSON.parse(raw) as { error?: string };
        if (typeof parsed.error === "string" && parsed.error.length > 0) {
          message = parsed.error;
        }
      } catch {
        // raw fallback
      }
    }
    throw new ApiClientError(message, { status: res.status, kind: "http" });
  }

  const payload = await readApiResponse<T>(res);
  if (payload.error) throw new ApiClientError(payload.error, { status: res.status, kind: "app" });
  return payload.data;
}

const withSession = <T extends { session: AuthSession }>(promise: Promise<T>): Promise<T> =>
  promise.then((value) => {
    setApiAuthSession(value.session);
    return value;
  });

export const apiClient = {
  getHealth: () => request<{ ok: boolean; now: string }>("/health"),

  authRegisterEmail: (body: { email: string; password: string; displayName?: string }) =>
    request<{ registered: boolean; requiresVerification: boolean; email: string }>("/auth/email/register", {
      method: "POST",
      body: JSON.stringify(body)
    }),
  authVerifyEmail: (body: { email: string; code: string }) =>
    withSession(
      request<{ session: AuthSession }>("/auth/email/verify", {
        method: "POST",
        body: JSON.stringify(body)
      })
    ),
  authLoginEmail: (body: { email: string; password: string; captchaToken?: string }) =>
    withSession(
      request<{ session: AuthSession }>("/auth/email/login", {
        method: "POST",
        body: JSON.stringify(body)
      })
    ),
  authOauthStart: (provider: "google" | "facebook") =>
    request<{ provider: "google" | "facebook"; state: string; url: string }>(`/auth/oauth/${provider}/start`, { method: "POST" }),
  authOauthCallback: (provider: "google" | "facebook", params: { state: string; email?: string; name?: string }) =>
    withSession(
      request<{ provider: "google" | "facebook"; session: AuthSession }>(
        `/auth/oauth/${provider}/callback?${new URLSearchParams({
          state: params.state,
          ...(params.email ? { email: params.email } : {}),
          ...(params.name ? { name: params.name } : {})
        }).toString()}`
      )
    ),
  authForgotPassword: (body: { email: string }) =>
    request<{ accepted: true }>("/auth/password/forgot", {
      method: "POST",
      body: JSON.stringify(body)
    }),
  authResetPassword: (body: { email: string; code: string; password: string }) =>
    request<{ reset: true }>("/auth/password/reset", {
      method: "POST",
      body: JSON.stringify(body)
    }),
  authRefresh: () =>
    withSession(
      request<{ session: AuthSession }>("/auth/refresh", {
        method: "POST"
      })
    ),
  authLogout: async () => {
    try {
      await request<{ loggedOut: true }>("/auth/logout", { method: "POST" }, { retryAuth: false });
    } finally {
      clearApiAuthSession();
    }
  },
  authMe: async () => {
    const result = await request<{ user: AuthUser }>("/auth/me", { method: "GET" });
    return result.user;
  },
  authTotpSetup: () => request<{ secret: string; uri: string } & MfaChallenge>("/auth/totp/setup", { method: "POST" }),
  authTotpVerify: (body: { code: string }) =>
    withSession(
      request<{ verified: boolean; session: AuthSession }>("/auth/totp/verify", {
        method: "POST",
        body: JSON.stringify(body)
      })
    ),

  getWorkspaces: () => request<Workspace[]>("/workspaces"),
  createWorkspace: (body: Pick<Workspace, "name" | "slug" | "industry" | "teamSize">) =>
    request<Workspace>("/workspaces", { method: "POST", body: JSON.stringify(body) }),
  patchWorkspace: (id: string, body: Partial<Pick<Workspace, "name" | "slug" | "industry" | "teamSize" | "themePreference">>) =>
    request<Workspace>(`/workspaces/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  switchWorkspace: (id: string) =>
    withSession(
      request<WorkspaceSwitchResult>(`/workspaces/${id}/switch`, {
        method: "POST"
      })
    ),

  getGoals: () => request<Goal[]>("/goals"),
  createGoal: (body: { title: string; targetDate?: string; successCriteria?: string; progressScore?: number }) =>
    request<Goal>("/goals", { method: "POST", body: JSON.stringify(body) }),
  getProjects: () => request<Project[]>("/projects"),
  createProject: (body: {
    title: string;
    description?: string;
    color?: string;
    icon?: string;
    goalIds?: string[];
    primaryGoalId?: string;
    memberIds?: string[];
    defaultAgentId?: string;
    connectedToolIds?: string[];
    recommendedAgentIds?: string[];
    siteDispatchPolicy?: Project["siteDispatchPolicy"];
  }) =>
    request<Project>("/projects", { method: "POST", body: JSON.stringify(body) }),
  getReports: (scope = "workspace") => request<ReportsSnapshot>(`/reports/${encodeURIComponent(scope)}`),
  getAgents: () => request<Agent[]>("/agents"),
  getDiscoverAgents: () => request<Agent[]>("/agents/discover"),
  testRemoteAgent: (body: RemoteAgentConnectionInput) =>
    request<RemoteAgentConnectionTest>("/agents/remote/test", { method: "POST", body: JSON.stringify(body) }),
  createRemoteAgent: (body: RemoteAgentConnectionInput & { name: string }) =>
    request<Agent>("/agents/remote", { method: "POST", body: JSON.stringify(body) }),
  updateRemoteAgent: (id: string, body: RemoteAgentUpdateInput) =>
    request<Agent>(`/agents/${id}/remote`, { method: "PATCH", body: JSON.stringify(body) }),
  createAgent: (body: Partial<Agent> & Pick<Agent, "name">) => request<Agent>("/agents", { method: "POST", body: JSON.stringify(body) }),
  hireAgentFromCatalog: (catalogId: string) => request<Agent>("/agents/hire", { method: "POST", body: JSON.stringify({ catalogId }) }),
  patchAgent: (id: string, body: Partial<Agent>) => request<Agent>(`/agents/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  connectAgent: (id: string, mode: "connect" | "disconnect") =>
    request<{ id: string; connected: boolean; status: Agent["status"] }>(`/agents/${id}/connect`, { method: "POST", body: JSON.stringify({ mode }) }),
  submitRemoteAgentApproval: (runId: string, choice: "once" | "deny") =>
    request<{ runId: string; choice: "once" | "deny" }>(`/agent-runs/${runId}/approval`, {
      method: "POST",
      body: JSON.stringify({ choice })
    }),
  getMembers: () => request<WorkspaceMember[]>("/members"),
  inviteMember: (body: { name: string; email: string; role: WorkspaceMember["role"]; invitedByUserId?: string }) =>
    request<{ member: WorkspaceMember; pendingAssignment: PendingAssignment }>("/members/invite", { method: "POST", body: JSON.stringify(body) }),
  getPendingAssignments: () => request<PendingAssignment[]>("/pending-assignments"),
  getNotifications: () => request<NotificationPreference[]>("/settings/notifications"),
  patchNotification: (id: string, enabled: boolean) =>
    request<NotificationPreference>(`/settings/notifications/${id}`, { method: "PATCH", body: JSON.stringify({ enabled }) }),
  getIntegrations: () => request<Integration[]>("/settings/integrations"),
  patchIntegration: (id: string, status: Integration["status"], detail?: string) =>
    request<Integration>(`/settings/integrations/${id}`, { method: "PATCH", body: JSON.stringify({ status, detail }) }),
  getAiModel: () => request<{ model: string }>("/settings/ai-model"),
  updateAiModel: (model: string) => request<{ model: string }>("/settings/ai-model", { method: "PUT", body: JSON.stringify({ model }) }),
  getAiSettings: () => request<AiSettings>("/settings/ai"),
  updateAiSettings: (body: {
    provider: AiProviderId;
    model: string;
    prompts: Record<SystemPromptKey, string>;
  }) => request<AiSettings>("/settings/ai", { method: "PUT", body: JSON.stringify(body) }),
  testAiProvider: () =>
    request<{ status: AiSettings["providerStatus"]; message: string }>("/settings/ai/test", { method: "POST" }),
  getCredits: () => request<{ balance: number }>("/credits"),
  getCreditsLedger: () => request<CreditLedger[]>("/credits/ledger"),
  purchaseCredits: (credits: number, priceLabel?: string) =>
    request<{ success: true; balance: number; entry: CreditLedger }>("/credits/purchase", {
      method: "POST",
      body: JSON.stringify({ credits, priceLabel })
    }),
  getTasks: () => request<Task[]>("/tasks"),
  executeSiteAction: (body: {
    instruction: string;
    mode?: "read" | "fill" | "agent";
    dispatchPolicy?: "plugin_first" | "plugin_only" | "extension_only";
    timeoutMs?: number;
    siteId?: string;
    siteUrl?: string;
    customerId?: string;
  }) =>
    request<{
      task_id: string;
      status: string;
      output: string;
      duration_ms: number;
      site_id: string;
      customer_id: string;
      origin?: string;
      execution_channel?: "wordpress_plugin" | "chrome_extension" | null;
      fallback_used?: boolean;
      attempts?: Array<{
        channel: "wordpress_plugin" | "chrome_extension";
        status: "completed" | "failed" | "timeout" | "extension_offline";
        error_code?: string | null;
        retriable?: boolean;
        duration_ms: number;
        output: string;
      }>;
      error_code?: string;
    }>("/site-agent/execute", {
      method: "POST",
      body: JSON.stringify({
        instruction: body.instruction,
        mode: body.mode,
        dispatch_policy: body.dispatchPolicy,
        timeout_ms: body.timeoutMs,
        site_id: body.siteId,
        site_url: body.siteUrl,
        customer_id: body.customerId
      })
    }),
  verifyWordpressIntegration: (body: { siteId?: string; siteUrl?: string; customerId?: string }) =>
    request<Integration>("/settings/integrations/wordpress/verify", {
      method: "POST",
      body: JSON.stringify({
        site_id: body.siteId,
        site_url: body.siteUrl,
        customer_id: body.customerId
      })
    }),
  disconnectWordpressIntegration: () => request<Integration>("/settings/integrations/wordpress/disconnect", { method: "POST" }),
  getTasksPage: (filters?: { status?: Task["status"]; projectId?: string; cursor?: string; limit?: number }) => {
    const params = new URLSearchParams();
    if (filters?.status) params.set("status", filters.status);
    if (filters?.projectId) params.set("projectId", filters.projectId);
    if (filters?.cursor) params.set("cursor", filters.cursor);
    if (typeof filters?.limit === "number") params.set("limit", String(filters.limit));
    const qs = params.toString();
    return request<{ items: Task[]; nextCursor?: string }>(qs.length > 0 ? `/tasks?${qs}` : "/tasks");
  },
  getSubtasks: (taskId?: string) => request<Subtask[]>(taskId ? `/subtasks?taskId=${encodeURIComponent(taskId)}` : "/subtasks"),
  getAtomicTasks: (taskId?: string) => request<AtomicTask[]>(taskId ? `/atomic-tasks?taskId=${encodeURIComponent(taskId)}` : "/atomic-tasks"),
  getActivity: (filters?: { projectId?: string; agentId?: string; taskId?: string; state?: ActivityEvent["state"]; cursor?: string; limit?: number }) => {
    const params = new URLSearchParams();
    if (filters?.projectId) params.set("projectId", filters.projectId);
    if (filters?.agentId) params.set("agentId", filters.agentId);
    if (filters?.taskId) params.set("taskId", filters.taskId);
    if (filters?.state) params.set("state", filters.state);
    if (filters?.cursor) params.set("cursor", filters.cursor);
    if (typeof filters?.limit === "number") params.set("limit", String(filters.limit));
    const qs = params.toString();
    return request<ActivityPage>(qs.length > 0 ? `/activity?${qs}` : "/activity");
  },
  getClarificationQuestions: (taskId: string) => request<{ questions: ClarificationQuestion[] }>(`/tasks/${taskId}/clarification-questions`),
  createTask: (body: CreateTaskInput & { clarityScore?: number }) =>
    request<{ task: Task; queued: boolean; operation?: TaskOperation; deduplicated?: boolean }>("/tasks", { method: "POST", body: JSON.stringify(body) }),
  patchTask: (id: string, body: Partial<Task> & { definitionOfDone?: string }) =>
    request<Task>(`/tasks/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  getTaskSnapshot: (id: string, options?: { signal?: AbortSignal }) => request<TaskSnapshot>(`/tasks/${id}/snapshot`, undefined, { signal: options?.signal }),
  getTaskOutput: (id: string) => request<TaskOutput>(`/tasks/${id}/output`),
  getTaskOperation: (taskId: string, operationId: string) => request<TaskOperation>(`/tasks/${taskId}/operations/${operationId}`),
  clarifyTask: (id: string, body: ClarificationPayload) =>
    request<{ taskId: string; operationId: string; status: TaskOperation["status"]; deduplicated?: boolean }>(`/tasks/${id}/clarify`, { method: "POST", body: JSON.stringify(body) }),
  generateSubtasks: (id: string) =>
    request<{ taskId: string; operationId: string; status: TaskOperation["status"]; deduplicated?: boolean }>(`/tasks/${id}/generate-subtasks`, { method: "POST" }),
  resolveBlocker: (id: string) =>
    request<{ taskId: string; operationId: string; status: TaskOperation["status"]; queued: boolean; deduplicated?: boolean }>(`/tasks/${id}/resolve-blocker`, { method: "POST" }),
  completeAtomicTask: (id: string) =>
    request<{ atomicTask: AtomicTask; taskStatus: Task["status"]; activated: number }>(`/atomic-tasks/${id}/complete`, { method: "POST" }),
  retryAtomicTask: (id: string) =>
    request<{ atomicTask: AtomicTask; taskStatus: Task["status"]; activated: number }>(`/atomic-tasks/${id}/retry`, { method: "POST" }),
  createSubtask: (taskId: string, body: { title: string; assignee?: Task["assignee"]; status?: Task["status"]; dependsOn?: string[] }) =>
    request<Subtask>(`/tasks/${taskId}/subtasks`, { method: "POST", body: JSON.stringify(body) }),
  patchSubtask: (subtaskId: string, body: { title?: string; assignee?: Task["assignee"]; status?: Task["status"]; dependsOn?: string[] }) =>
    request<Subtask>(`/subtasks/${subtaskId}`, { method: "PATCH", body: JSON.stringify(body) }),
  createAtomicTask: (
    subtaskId: string,
    body: { title: string; assignee?: Task["assignee"]; status?: Task["status"]; dependsOn?: string[] }
  ) => request<AtomicTask>(`/subtasks/${subtaskId}/atomic-tasks`, { method: "POST", body: JSON.stringify(body) }),
  patchAtomicTask: (
    atomicTaskId: string,
    body: { title?: string; assignee?: Task["assignee"]; status?: Task["status"]; dependsOn?: string[]; lastError?: string }
  ) => request<AtomicTask>(`/atomic-tasks/${atomicTaskId}`, { method: "PATCH", body: JSON.stringify(body) }),
  patchProject: (
    id: string,
    body: Partial<
      Pick<
        Project,
        "title" | "description" | "color" | "icon" | "defaultAgentId" | "goalIds" | "primaryGoalId" | "memberIds" | "connectedToolIds" | "recommendedAgentIds" | "siteDispatchPolicy"
      >
    >
  ) => request<Project>(`/projects/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  getArchive: (filters?: { entityType?: "goal" | "project" | "task"; search?: string; cursor?: string; limit?: number }) => {
    const params = new URLSearchParams();
    if (filters?.entityType) params.set("entityType", filters.entityType);
    if (filters?.search) params.set("search", filters.search);
    if (filters?.cursor) params.set("cursor", filters.cursor);
    if (typeof filters?.limit === "number") params.set("limit", String(filters.limit));
    const qs = params.toString();
    return request<ArchivePage>(qs.length > 0 ? `/archive?${qs}` : "/archive");
  },
  archiveTask: (id: string) => request<{ archived: boolean; id: string }>(`/archive/task/${id}`, { method: "POST" }),
  restoreTask: (id: string) => request<{ restored: boolean; id: string }>(`/restore/task/${id}`, { method: "POST" }),
  archiveEntity: (entityType: "goal" | "project" | "task", id: string) => request<{ archived: boolean; id: string }>(`/archive/${entityType}/${id}`, { method: "POST" }),
  restoreEntity: (entityType: "goal" | "project" | "task", id: string) => request<{ restored: boolean; id: string }>(`/restore/${entityType}/${id}`, { method: "POST" }),

  superAdminOverview: () => request<SuperAdminOverview>("/superadmin/overview"),
  superAdminUsers: (query?: string) => request<SuperAdminUser[]>(query ? `/superadmin/users?query=${encodeURIComponent(query)}` : "/superadmin/users"),
  superAdminUserAction: (userId: string, action: "suspend" | "delete" | "reset_password" | "force_reauth") =>
    request<{ success: boolean; action: string; userId: string }>(`/superadmin/users/${userId}/actions`, {
      method: "POST",
      body: JSON.stringify({ action })
    }),
  superAdminWorkspaces: () => request<SuperAdminWorkspace[]>("/superadmin/workspaces"),
  superAdminWorkspaceAction: (workspaceId: string, action: "suspend" | "transfer" | "delete", targetOwnerUserId?: string) =>
    request<{ success: boolean; action: string; workspaceId: string }>(`/superadmin/workspaces/${workspaceId}/actions`, {
      method: "POST",
      body: JSON.stringify({ action, targetOwnerUserId })
    }),
  superAdminLlmConfig: () => request<LlmRoutingConfig>("/superadmin/llm-config"),
  superAdminPatchLlmConfig: (patch: Partial<LlmRoutingConfig>) =>
    request<{ updated: true }>("/superadmin/llm-config", {
      method: "PATCH",
      body: JSON.stringify(patch)
    }),
  superAdminByok: () => request<ByokGrant[]>("/superadmin/byok"),
  superAdminPatchByok: (userId: string, enabled: boolean) =>
    request<{ userId: string; enabled: boolean }>(`/superadmin/byok/${userId}`, {
      method: "PATCH",
      body: JSON.stringify({ enabled })
    }),
  superAdminSystemHealth: () => request<SystemHealthSnapshot>("/superadmin/system-health"),
  superAdminAudit: () => request<SuperAdminAuditEvent[]>("/superadmin/audit"),
  superAdminIpAllowlist: () => request<IpAllowlistEntry[]>("/superadmin/ip-allowlist"),
  superAdminAddIpAllowlist: (cidr: string, description?: string) =>
    request<{ added: true; cidr: string }>("/superadmin/ip-allowlist", {
      method: "POST",
      body: JSON.stringify({ cidr, description })
    }),
  superAdminPatchIpAllowlist: (id: string, enabled: boolean) =>
    request<{ updated: true; id: string; enabled: boolean }>(`/superadmin/ip-allowlist/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ enabled })
    })
};
